import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { usePlan } from "@/context/PlanContext";
import api from "@/lib/api";
import { formatDateDDMMYYYY as fmtDate } from "@/lib/format";
import { Lock, X, Check, ShieldCheck, Loader2, Gift, Users, Star } from "lucide-react";

const CAT_LABEL = { balvatika: "Balvatika", primary: "Primary (I–V)", upper_primary: "Upper Primary (VI–VIII)", high_school: "High School (IX–X)" };
const CAT_COLOR = { balvatika: "#DB2777", primary: "#2563EB", upper_primary: "#059669", high_school: "#EA580C" };
const CATS = ["balvatika", "primary", "upper_primary", "high_school"];

const Blur = ({ w = "w-16" }) => (
  <span className={`inline-block ${w} align-middle blur-[3px] select-none text-thali-ink/40 font-num`} aria-hidden="true">₹▓▓▓.▓▓</span>
);

export function LockedReport({ date, counts, menu }) {
  const { t } = useLang();
  const total = CATS.reduce((a, c) => a + (parseInt(counts[c] || 0, 10) || 0), 0);
  const classes = CATS.filter((c) => (parseInt(counts[c] || 0, 10) || 0) > 0).length;
  return (
    <div className="space-y-4" data-testid="locked-report">
      <div className="rounded-card bg-gradient-to-br from-thali-green to-thali-green/85 text-white p-5 text-center shadow-warm">
        <div className="w-14 h-14 rounded-full bg-white/15 grid place-items-center mx-auto mb-2"><Lock className="w-7 h-7" /></div>
        <div className="font-heading font-extrabold text-xl">🔒 {t("pw_ready_title").replace("{date}", fmtDate(date))}</div>
        <div className="text-sm opacity-90 mt-1">{t("pw_ready_sub").replace("{students}", total).replace("{classes}", classes)}</div>
      </div>

      <div className="rounded-card bg-thali-paper border border-thali-turmeric/20 shadow-warm p-4 flex items-center justify-between">
        <div>
          <div className="text-xs text-thali-ink/60">{t("grand_total")}</div>
          <div className="text-3xl font-num font-extrabold"><Blur w="w-24" /></div>
        </div>
        <div className="text-right text-sm text-thali-ink/60"><div>🍽️ {menu}</div><div className="mt-1">{total} {t("students")}</div></div>
      </div>

      {CATS.filter((c) => (parseInt(counts[c] || 0, 10) || 0) > 0).map((c) => (
        <div key={c} className="rounded-card bg-thali-paper border border-thali-turmeric/20 shadow-warm overflow-hidden" data-testid={`locked-cat-${c}`}>
          <div className="flex items-center justify-between px-4 py-3 text-white" style={{ background: CAT_COLOR[c] }}>
            <span className="font-heading font-bold">{CAT_LABEL[c]} · {counts[c]}</span>
            <Blur />
          </div>
          <div className="divide-y divide-thali-turmeric/10">
            {[0, 1, 2].map((i) => (
              <div key={i} className="px-4 py-2 flex items-center justify-between text-sm">
                <span className="blur-[3px] select-none text-thali-ink/40">▓▓▓▓▓▓ ▓▓▓</span>
                <Blur w="w-14" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function PaywallSheet({ open, onClose, logEvent }) {
  const { t } = useLang();
  const nav = useNavigate();
  const { payNow, paying } = usePlan();
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    if (open) api.get("/paywall/config").then(({ data }) => setCfg(data)).catch(() => {});
  }, [open]);

  if (!open) return null;
  const price = cfg?.price ?? 99;
  const perks = [t("pw_perk1"), t("pw_perk2"), t("pw_perk3"), t("pw_perk4"), t("pw_perk5")];

  const pay = () => { logEvent("pay_tapped"); payNow({ onFailure: () => logEvent("payment_failed") }); };
  const refer = () => { logEvent("refer_tapped"); onClose(); nav("/app/refer"); };
  const close = () => { logEvent("closed"); onClose(); };

  return (
    <div className="fixed inset-0 z-[90] flex items-end md:items-center justify-center" data-testid="paywall-sheet">
      <div className="absolute inset-0 bg-black/40" onClick={close} />
      <div className="relative w-full md:max-w-[480px] bg-thali-cream rounded-t-3xl md:rounded-3xl shadow-2xl paywall-slide max-h-[75vh] md:max-h-[85vh] overflow-y-auto">
        <button onClick={close} className="absolute right-4 top-4 z-10 w-9 h-9 rounded-full bg-white/80 grid place-items-center text-thali-ink/60" aria-label={t("pw_maybe_later")} data-testid="paywall-close"><X className="w-5 h-5" /></button>
        <div className="p-5 pt-6">
          <div className="w-12 h-1.5 bg-thali-turmeric/40 rounded-full mx-auto mb-4 md:hidden" />

          <h2 className="font-heading font-extrabold text-2xl text-thali-green text-center leading-tight">{cfg?.headline || `Unlock everything for just ₹${price}/year`}</h2>
          <div className="text-center mt-2">
            <span className="inline-block bg-thali-turmeric/40 text-thali-ink font-heading font-extrabold text-lg rounded-xl px-3 py-1" data-testid="paywall-perday">{cfg?.perday || "= only 27 paise per day ☕"}</span>
          </div>
          <p className="text-sm text-thali-ink/70 text-center mt-2">{cfg?.subline || "Save 15+ minutes every school day."}</p>

          <ul className="mt-4 space-y-2">
            {perks.map((p, i) => (
              <li key={i} className="flex items-start gap-2 text-sm"><Check className="w-4 h-4 text-thali-green mt-0.5 shrink-0" /> {p}</li>
            ))}
          </ul>

          {(cfg?.subscribers || cfg?.this_week) && (
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-sm" data-testid="paywall-social">
              {cfg.subscribers ? <span className="inline-flex items-center gap-1 bg-white rounded-full px-3 py-1 font-semibold text-thali-green"><Users className="w-4 h-4" /> {t("pw_joined").replace("{n}", cfg.subscribers.toLocaleString("en-IN"))}</span> : null}
              {cfg.this_week ? <span className="inline-flex items-center gap-1 bg-white rounded-full px-3 py-1 font-semibold text-thali-ink/70">{t("pw_this_week").replace("{n}", cfg.this_week)}</span> : null}
            </div>
          )}

          {cfg?.testimonials?.length > 0 && (
            <div className="mt-4 space-y-2" data-testid="paywall-testimonials">
              {cfg.testimonials.map((tm, i) => (
                <div key={i} className="bg-white rounded-xl p-3 text-sm">
                  <div className="flex items-center gap-1 text-thali-turmericdark">{[0, 1, 2, 3, 4].map((s) => <Star key={s} className="w-3.5 h-3.5 fill-current" />)}</div>
                  <div className="text-thali-ink/80 mt-1">“{tm.quote}”</div>
                  <div className="text-xs text-thali-ink/50 mt-1 font-semibold">— {tm.name}{tm.place ? `, ${tm.place}` : ""}</div>
                </div>
              ))}
            </div>
          )}

          <button onClick={pay} disabled={paying} data-testid="paywall-pay-btn"
            className="btn-3d-saffron w-full mt-5 shine-btn relative overflow-hidden" style={{ minHeight: 60 }}>
            {paying ? <><Loader2 className="w-5 h-5 animate-spin" /> {t("processing")}</> : <>🔓 {t("pw_unlock_btn").replace("{price}", price)}</>}
          </button>
          <div className="text-center text-xs text-thali-ink/50 mt-2">UPI · Cards · Net Banking · {t("secure_razorpay")} 🔒</div>

          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-thali-ink/60 mt-3">
            <a href="/refund" target="_blank" rel="noreferrer" className="hover:underline" data-testid="paywall-refund-link">✅ {t("pw_refund")}</a>
            <span>🔒 {t("pw_secure")}</span>
            <a href="mailto:TeacherUtilityToolKit@gmail.com" className="hover:underline">📞 TeacherUtilityToolKit@gmail.com</a>
          </div>

          <button onClick={refer} className="w-full mt-4 text-sm font-bold text-thali-green flex items-center justify-center gap-2 py-2" data-testid="paywall-refer-btn">
            <Gift className="w-4 h-4" /> {t("pw_refer_alt")}
          </button>
          <button onClick={() => { onClose(); nav("/pay-qr"); }} className="w-full mt-1 text-sm font-bold text-thali-ink/70 flex items-center justify-center gap-2 py-2" data-testid="paywall-qr-btn">
            📷 {t("qr_or_scan")}
          </button>
          <button onClick={close} className="w-full mt-1 text-sm text-thali-ink/50 py-2" data-testid="paywall-later-btn">{t("pw_maybe_later")}</button>
        </div>
      </div>
    </div>
  );
}
